import React from 'react';
import { Typography, Stack } from '@mui/material';

interface ConfidenceIndicatorProps {
  supportingCount: number;
  contradictingCount: number;
  neutralCount: number;
  overallConfidence: number;
}

const getConfidenceLabel = (confidence: number, total: number): string => {
  if (total === 0) return 'Insufficient evidence';
  if (confidence >= 0.75) return 'Strong support';
  if (confidence >= 0.6) return 'Moderate support';
  if (confidence >= 0.4 && confidence <= 0.6) return 'Mixed evidence';
  if (confidence >= 0.25) return 'Moderate contradiction';
  return 'Strong contradiction';
};

const ConfidenceIndicator: React.FC<ConfidenceIndicatorProps> = ({
  supportingCount,
  contradictingCount,
  neutralCount,
  overallConfidence,
}) => {
  const total = supportingCount + contradictingCount + neutralCount;

  if (total === 0) {
    return (
      <div style={{ padding: 16, textAlign: 'center' }}>
        <Typography variant="body2" color="text.secondary">
          No evidence found. Try a different thesis or broaden your search.
        </Typography>
      </div>
    );
  }

  const supportPct = total > 0 ? (supportingCount / total) * 100 : 0;
  const contradictPct = total > 0 ? (contradictingCount / total) * 100 : 0;
  const neutralPct = total > 0 ? (neutralCount / total) * 100 : 0;

  const label = getConfidenceLabel(overallConfidence, total);

  return (
    <div style={{ width: '100%' }}>
      {/* Confidence bar */}
      <div
        style={{
          display: 'flex',
          width: '100%',
          height: 12,
          borderRadius: 4,
          overflow: 'hidden',
          marginBottom: 8,
        }}
      >
        {supportPct > 0 && (
          <div
            style={{
              width: `${supportPct}%`,
              backgroundColor: '#4DC4D2',
              transition: 'width 0.5s ease-in-out',
            }}
          />
        )}
        {neutralPct > 0 && (
          <div
            style={{
              width: `${neutralPct}%`,
              backgroundColor: '#D2D2D2',
              transition: 'width 0.5s ease-in-out',
            }}
          />
        )}
        {contradictPct > 0 && (
          <div
            style={{
              width: `${contradictPct}%`,
              backgroundColor: '#E94257',
              transition: 'width 0.5s ease-in-out',
            }}
          />
        )}
      </div>

      {/* Labels */}
      <Stack direction="row" justifyContent="space-between" alignItems="center">
        <Stack direction="row" spacing={2}>
          <Stack direction="row" spacing={0.5} alignItems="center">
            <div style={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: '#4DC4D2' }} />
            <Typography variant="caption">
              Supporting ({supportingCount})
            </Typography>
          </Stack>
          <Stack direction="row" spacing={0.5} alignItems="center">
            <div style={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: '#D2D2D2' }} />
            <Typography variant="caption">
              Neutral ({neutralCount})
            </Typography>
          </Stack>
          <Stack direction="row" spacing={0.5} alignItems="center">
            <div style={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: '#E94257' }} />
            <Typography variant="caption">
              Contradicting ({contradictingCount})
            </Typography>
          </Stack>
        </Stack>

        <Typography variant="caption" fontWeight={600} color="text.secondary">
          {label}
        </Typography>
      </Stack>
    </div>
  );
};

export default ConfidenceIndicator;
